import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { Children, cloneElement, forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { tabbable } from 'tabbable';
import ResizeObserver from 'resize-observer-polyfill';
import { Box } from '../Box';
import { useBoxSize } from '../system';
import { getRect } from '../utils';
import { Button } from './Button';

function getFixedValue(value) {
  // Safari doesn't like sub pixels
  return parseInt(value.toFixed(0), 10) + 1;
}

var ButtonInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      style = _ref.style,
      size = _ref.size,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      restProps = _objectWithoutPropertiesLoose(_ref, ["children", "style", "size", "onOpen", "onClose"]);

  var containerRef = useRef();
  var inputRef = useRef();
  var buttonRef = useRef();

  var _useState = useState(false),
      isOpen = _useState[0],
      setOpen = _useState[1];

  var _useBoxSize = useBoxSize(size),
      boxSize = _useBoxSize.boxSize;

  var containerProps = {
    innerRef: containerRef,
    height: boxSize,
    position: 'relative',
    overflow: 'hidden',
    transition: 'width 200ms cubic-bezier(.02,.8,.38,1)'
  };
  var inputContainerProps = {
    innerRef: inputRef,
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    opacity: isOpen ? 1 : 0,
    transition: 'opacity 180ms ease'
  };
  var inputProps = {
    size: size,
    secondaryStroke: false,
    disabled: !isOpen,
    onBlur: function onBlur() {
      var input = inputRef.current.querySelector('input');

      if (input.value.length === 0) {
        close();
      }
    },
    onKeyDown: function onKeyDown(event) {
      if (event.key === 'Escape') {
        close();
      }
    }
  };
  var buttonContainerProps = {
    innerRef: buttonRef,
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    opacity: isOpen ? 0 : 1,
    transition: 'opacity 180ms ease'
  };

  var buttonProps = _objectSpread(_objectSpread({}, restProps), {}, {
    size: size,
    disabled: isOpen,
    onClick: open
  });

  function open() {
    setOpen(true);
    var input = inputRef.current.querySelector('input');

    if (input && input.focus) {
      setTimeout(function () {
        return input.focus();
      });
    }

    if (onOpen) {
      onOpen();
    }
  }

  function close() {
    setOpen(false);

    var _tabbable = tabbable(buttonRef.current),
        button = _tabbable[0];

    if (button && button.focus) {
      setTimeout(function () {
        return button.focus();
      });
    }

    if (onClose) {
      onClose();
    }
  }

  function transition() {
    var container = containerRef.current;
    var inputChild = inputRef.current.firstChild;
    var buttonChild = buttonRef.current.firstChild;
    inputChild.style.width = '';
    buttonChild.style.width = '';
    var inputRect = getRect(inputChild, {
      clone: true
    });
    var buttonRect = getRect(buttonChild, {
      clone: true
    });
    var containerWidth = getFixedValue(isOpen ? inputRect.width : buttonRect.width);
    container.style.width = containerWidth + 'px';
    inputChild.style.width = '100%';
    buttonChild.style.width = '100%';
  }

  useLayoutEffect(function () {
    transition();
    var ro = new ResizeObserver(transition);
    ro.observe(isOpen ? inputRef.current : buttonRef.current);
    return function () {
      ro.disconnect();
    };
  }, [isOpen]);
  useImperativeHandle(ref, function () {
    return {
      open: open,
      close: close
    };
  }, []); // @ts-ignore

  var button = /*#__PURE__*/React.createElement(Button, buttonProps);
  return /*#__PURE__*/React.createElement(Box, containerProps, /*#__PURE__*/React.createElement(Box, inputContainerProps, typeof children === 'function' ? children({
    inputProps: inputProps,
    isOpen: isOpen,
    open: open,
    close: close
  }) : /*#__PURE__*/cloneElement(Children.only(children), inputProps)), /*#__PURE__*/React.createElement(Box, buttonContainerProps, button));
});
ButtonInput.displayName = 'Button.Input';
export default ButtonInput;